// kan-97: the commission sheets are copied out of the app and pasted straight
// into Excel. A plain-text (tab-separated) clipboard carries the values but
// loses formatting, so a colored cell has to travel as an HTML flavour too —
// Excel reads `text/html` on paste and keeps the inline font color.
//
// Excel prefers the HTML flavour whenever one is present, and pasting HTML
// applies the source cell format (General) instead of the destination's own
// number formats. So the HTML flavour is only added when a cell actually
// carries a color; an uncolored sheet stays a plain-text copy, byte-for-byte
// what the modals copied before.
//
// Shared by the kan-88 single-row export and the kan-89 weekly-activity sheet
// so both have exactly one clipboard code path.

export interface ISheetCell {
  text: string;
  /** CSS hex font color for this cell's text; omitted means Excel's default. */
  color?: string;
}

/** Excel's standard "Light Blue" — the executive's own sheet convention. */
export const SHEET_FONT_BLUE = '#00B0F0';
/** Excel's standard "Red" — the sheet's default for every other temp name. */
export const SHEET_FONT_RED = '#FF0000';

// The color lands unescaped inside a style attribute (and innerHTML in the
// fallback), so only a plain hex color is ever emitted.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const cellColor = (cell: ISheetCell | undefined): string | undefined =>
  cell?.color && HEX_COLOR.test(cell.color) ? cell.color : undefined;

// Free-text fields (job title, company name...) can hold tabs/newlines, which
// would shift or split the pasted Excel row — flatten them. Nothing else is
// normalized: the plain-text flavour must stay byte-identical to what the
// modals copied before this helper existed.
const flatten = (text: string): string =>
  (text || '').replace(/[\t\r\n]+/g, ' ').trim();

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** The tab-separated text flavour — what the modals have always copied. */
export const sheetPlainText = (rows: Array<Array<ISheetCell>>): string =>
  rows
    .map((row) => row.map((cell) => flatten(cell?.text ?? '')).join('\t'))
    .join('\n');

/** True when at least one cell needs formatting Excel can only get via HTML. */
export const sheetHasColor = (rows: Array<Array<ISheetCell>>): boolean =>
  rows.some((row) => row.some((cell) => Boolean(cellColor(cell))));

/**
 * The HTML flavour: a full document, since Excel ignores a bare fragment.
 * Only colored cells carry a style attribute so uncolored cells inherit the
 * destination sheet's own formatting.
 */
export const sheetHtml = (rows: Array<Array<ISheetCell>>): string => {
  const body = rows
    .map((row) => {
      const cells = row
        .map((cell) => {
          const color = cellColor(cell);
          const style = color ? ` style="color:${color}"` : '';
          return `<td${style}>${escapeHtml(flatten(cell?.text ?? ''))}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<html><head><meta charset="utf-8"></head><body><table><tbody>${body}</tbody></table></body></html>`;
};

/**
 * Copies the sheet, adding the HTML flavour only when a cell is colored.
 * Returns false only when every path failed, so the caller can decide what
 * to tell the user.
 *
 * Must be called synchronously from the click handler: Safari only honours a
 * ClipboardItem constructed inside the user gesture.
 */
export const copySheetToClipboard = async (
  rows: Array<Array<ISheetCell>>
): Promise<boolean> => {
  const text = sheetPlainText(rows);

  if (!sheetHasColor(rows)) {
    return copyPlainText(text);
  }

  const html = sheetHtml(rows);
  try {
    // Built before the await so the gesture is still active in Safari.
    const item = new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    });
    await navigator.clipboard.write([item]);
    return true;
  } catch {
    // ClipboardItem / clipboard.write can be missing (http, older Safari,
    // permissions). Copying a selected DOM table natively puts BOTH the HTML
    // and text flavours on the clipboard, so the color still survives.
    return copyViaHiddenTable(html);
  }
};

const copyPlainText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API can be unavailable (http, permissions) — fall back.
    return copyViaHiddenTextarea(text);
  }
};

const copyViaHiddenTextarea = (text: string): boolean => {
  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    if (textarea && textarea.parentNode) textarea.parentNode.removeChild(textarea);
  }
};

const copyViaHiddenTable = (html: string): boolean => {
  let host: HTMLDivElement | null = null;
  try {
    host = document.createElement('div');
    host.innerHTML = html;
    // Off-screen rather than display:none — a hidden node can't be selected.
    host.style.position = 'fixed';
    host.style.left = '-99999px';
    host.style.top = '0';
    document.body.appendChild(host);

    const range = document.createRange();
    range.selectNodeContents(host);
    const selection = window.getSelection();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);

    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    if (host && host.parentNode) host.parentNode.removeChild(host);
  }
};
